/* eslint-disable max-classes-per-file */
import { IModel, Collection, IViolation, IResourceContext } from '../base/annotation';
import { Metamodel } from '../base/model';
import { Named, Owned } from '../base/named';
import { OwnedSet } from '../base/ownedset';
import { ActivityPolicy } from './activitypolicy';
import { Pipeline } from './pipeline';
import { UserProperty } from '../base/userProperty';
import { format, sanitizeAndFormatString } from '../../shared/utils/string.util';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { ValidationConstants } from '../../shared/validation-constants';
import { UserPropertiesUtils } from '../../shared/utils/user-properties.util';

export enum ActivityScan {
    UNSEEN,
    SEEN,
    SCANNED
}

export enum Criteria {
    SUCCESS,
    FAILURE,
    COMPLETED,
    SKIPPED,
    INVALID
}

export type ActivityStateType = 'Active' | 'Inactive';
export type InactiveStatusType = 'Skipped' | 'Succeeded' | 'Failed';

export interface IVersioningActivity extends Activity {
    connectionVersion?: string;
    shouldHonorConnectionVersion?: () => boolean;
}

@Metamodel.model({
    name: 'activity', // As this is abstract this will never be used
    display: [shellStringResources.Activity, shellStringResources.Activities],
    referable: true,
    abstract: false,
    constraints: {
        name: {
            required: true,
            patterns: [
                {
                    message: format(shellStringResources.Validator_NamePattern_Start_AlphanumericUnderscore, shellStringResources.Activity),
                    regex: ValidationConstants.startWithAlphaNumericUnderscoreValidator
                },
                {
                    message: format(shellStringResources.Validator_NameMaxLength, shellStringResources.Activity, ValidationConstants.maxActivityNameLength.toString()),
                    regex: ValidationConstants.activityNameLengthValidator
                },
                {
                    message: format(shellStringResources.Validator_NamePattern_All_AlphanumericUnderscoreDashSpace, shellStringResources.Activity),
                    regex: ValidationConstants.allSupportedCharValidator
                },
                {
                    message: format(shellStringResources.Validator_NamePattern_No_TrailingSpace, shellStringResources.Activity),
                    regex: ValidationConstants.noTrailingSpaceValidator

                }
            ]
        }
    }
})
export abstract class Activity extends Named {

    public static readonly MAX_USER_PROPERTIES = 5;
    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        transient: true
    })
    public _visited: ActivityScan = ActivityScan.UNSEEN;

    @Metamodel.attribute({
        transient: true
    })
    public _level = 0;

    /**
     * The difference between activity types is that ExecutionActivity contains a "policy" for execution, containing propertites like "timeout".
     */
    public activityType: 'ControlActivity' | 'ExecutionActivity' = 'ExecutionActivity';

    @Metamodel.attribute({
        type: Activity.referTypeActivityPolicy
    })
    public policy = Owned.create<ActivityPolicy>(ActivityPolicy.MODEL, this);

    @Metamodel.attribute({
        reference: true,
        collection: Collection.Set,
        type: Activity.referTypeLink
    })
    public readonly fromLinks = OwnedSet.create<Link>(this);

    @Metamodel.attribute({
        collection: Collection.Set,
        type: Activity.referTypeLink
    })
    public readonly toLinks = OwnedSet.create<Link>(this);

    @Metamodel.attribute({
        constraint: {
            required: false,
            validator: Activity.validateUserProperties
        },
        display: shellStringResources.UserPropertiesLabel,
        collection: Collection.Set,
        type: Activity.referTypeUserProperty
    })
    public userProperties = OwnedSet.create<UserProperty>(this);

    public state: ActivityStateType;
    public onInactiveMarkAs: InactiveStatusType;

    constructor(name?: string, description?: string) {
        super(name, description);
        this.raw = `{
            "typeProperties": {
            }
        }`;
    }

    private static validateUserProperties(activity: Activity): IViolation[] | undefined {
        const nullViolations = Activity.validateNoneNullUserProperties(activity);
        const noneUniqueViolation = Activity.validateUniqueUserProperties(activity);
        const variableViolations = Activity.validateVariablesUserProperties(activity);
        const maxUserPropertiesViolations = Activity.validateMaxUserProperties(activity);
        const allViolations = [...nullViolations, ...noneUniqueViolation, ...maxUserPropertiesViolations, ...variableViolations];
        return allViolations.length > 0 ? allViolations : undefined;
    }

    private static validateNoneNullUserProperties(activity: Activity): IViolation[] {
        const violations: IViolation[] = [];
        const nullKeysExist = UserPropertiesUtils.nullKeyExists(activity.userProperties);
        if (nullKeysExist) {
            violations.push({
                context: activity,
                message: () => shellStringResources.NullNamesInUserPropertiesNotAllowed,
                logDetails: {
                    functionName: 'Activity.validateNoneNullUserProperties',
                    errorType: 'Required',
                    details: {
                        activityName: activity.name,
                        activityType: activity.activityType
                    }
                }
            });
        }
        const nullValuesExist = UserPropertiesUtils.nullValueExists(activity.userProperties);
        if (nullValuesExist) {
            violations.push({
                context: activity,
                message: () => shellStringResources.NullValuesInUserPropertiesNotAllowed,
                logDetails: {
                    functionName: 'Activity.validateNoneNullUserProperties',
                    errorType: 'Required',
                    details: {
                        activityName: activity.name,
                        activityType: activity.activityType
                    }
                }
            });
        }
        return violations;
    }

    private static validateUniqueUserProperties(activity: Activity): IViolation[] {
        const violations: IViolation[] = [];
        const noneUniqueNames = UserPropertiesUtils.getNoneUniqueNames(activity.userProperties);
        noneUniqueNames.forEach((userProperty: string) => {
            violations.push({
                context: activity,
                message: () => sanitizeAndFormatString(shellStringResources.MultipleUserPropertiesWithSameName, userProperty),
                logDetails: {
                    functionName: 'Activity.validateUniqueUserProperties',
                    errorType: 'DuplicatedName',
                    details: {
                        activityName: activity.name,
                        activityType: activity.activityType
                    }
                }
            });
        });
        return violations;
    }

    /**
     * User properties are user defined and need to be validated.
     * This checks if the properties contain undefined pipeline variables.
     * @param activity The activity
     * @returns Array of violations. Each user property can have multiple violations.
     */
    private static validateVariablesUserProperties(activity: Activity): IViolation[] {
        const violations: IViolation[] = [];
        // Note: We can modify getErrorUserPropertyVariablesMap for other Logic apps params (parameters, global parameters, etc.)
        // Currently, user properties are not in Fabric, so very low priority.
        const pipelineVariables = (activity.rootOwner as Pipeline).variables;
        const errorUserPropertyVariableMap = UserPropertiesUtils.getErrorUserPropertyVariablesMap(activity.userProperties, pipelineVariables);
        for (const [userProperty, errorVariables] of errorUserPropertyVariableMap) {
            errorVariables.forEach((variable: string) => {
                if (variable.startsWith(UserPropertiesUtils.expressionSyntaxError)) {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(shellStringResources.UserPropertyParseError, userProperty)
                    });
                } else {
                    violations.push({
                        context: activity,
                        message: () => sanitizeAndFormatString(shellStringResources.UndefinedVariableUserProperties, userProperty, variable)
                    });
                }
            });
        }
        return violations;
    }

    private static validateMaxUserProperties(activity: Activity): IViolation[] {
        const propertiesSize = activity.userProperties.size;
        const violations: IViolation[] = [];
        if (propertiesSize > Activity.MAX_USER_PROPERTIES) {
            violations.push({
                context: activity,
                message: () => format(shellStringResources.MaximumUserPropertiesExceeded, `${Activity.MAX_USER_PROPERTIES}`),
                logDetails: {
                    functionName: 'Activity.validateUniqueUserProperties',
                    errorType: 'MaxSize',
                    details: {
                        activityName: activity.name,
                        activityType: activity.activityType,
                        size: propertiesSize
                    }
                }
            });
        }
        return violations;
    }

    private static referTypeActivityPolicy(): IModel {
        return ActivityPolicy.MODEL;
    }

    private static referTypeLink(): IModel {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        return Link.MODEL;
    }

    private static referTypeUserProperty(): IModel {
        return UserProperty.MODEL;
    }

    public get owner(): Pipeline {
        return (super.getOwner() as Pipeline);
    }

    public get hasFromLinks(): boolean {
        return this.fromLinks.size > 0;
    }

    public get hasToLinks(): boolean {
        return this.toLinks.size > 0;
    }

    public get toActivities(): Set<Activity> {
        return new Set<Activity>(); // TODO
    }

    public linkTo(toActivity: Activity, criteria: Criteria = Criteria.COMPLETED): Link {
        // The owners for both of them should be the same
        if (this.owner !== toActivity.owner) {
            throw new Error('Cannot link activities in different pipelines');
        }
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        const link = new Link(this, toActivity, criteria);
        const toLink = this.toLinks.find((value) => value.fromActivity === this &&
            value.criteria === criteria &&
            value.toActivity === toActivity);

        if (toLink) {
            throw new Error('Cannot add the same type of link to the activity');
        }
        this.toLinks.add(link);
        toActivity.fromLinks.add(link);
        return link;
    }

    public removeLink(toActivity: Activity, criteria: Criteria = Criteria.COMPLETED): void {
        const toLink = this.toLinks.find((value) => value.fromActivity === this &&
            value.criteria === criteria &&
            value.toActivity === toActivity);

        if (toLink) {
            this.toLinks.delete(toLink);
            toActivity.fromLinks.delete(toLink);
        }
    }

    public isAncestor(mother: Activity): boolean {
        // TODO graph scan. For validation in expressions(if construct)
        mother = mother;
        return true;
    }

    public isDescendant(kid: Activity): boolean {
        // TODO graph scan. For validation in expressions(if construct)
        kid = kid;
        return false;
    }

    public addUserProperties(property: string, contextData: IResourceContext): void {
        return;
    }

    /*
      do some additional hand when cloning the activity
    */
    public onActivityCloned(clonedActivity: Activity, context: { inlineDataset?: boolean }): void {}
}

@Metamodel.model({
    referable: true
})
export class Link extends Owned {

    public static readonly MODEL: IModel;

    @Metamodel.attribute({
        reference: true,
        type: Link.referTypeActivity
    })
    public readonly fromActivity: Activity;

    @Metamodel.attribute({
        reference: true,
        type: Link.referTypeActivity
    })
    public readonly toActivity: Activity;

    @Metamodel.attribute({
        constraint: {
            validator: Link.validateCriteria
        }
    })
    public readonly criteria: Criteria;

    /*
       Immutable once created. If relinking is supported then we will support mutating methods
    */
    public constructor(fromActivity: Activity, toActivity: Activity, criteria: Criteria = Criteria.COMPLETED) {
        super();
        this.fromActivity = fromActivity;
        this.toActivity = toActivity;
        this.criteria = criteria;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static validateCriteria(owner: any, value: Criteria): IViolation[] | undefined {
        if (value === Criteria.INVALID) {
            return [{
                context: owner,
                message: () => sanitizeAndFormatString(shellStringResources.LinkCriteriaValidationMessage, owner.fromActivity.name, owner.toActivity.name),
                logDetails: {
                    functionName: 'Activity.validateCriteria',
                    errorType: 'Other'
                }
            }];
        }
        return undefined;
    }

    private static referTypeActivity(): IModel {
        return Activity.MODEL;
    }
}
